import { Button } from '@/components/ui/button';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import {
  useCreateTableViewChatService,
  useGetTableViewChatService,
  useTableViewChat,
} from '@/services/tableViews';
import { Loader2, Minimize2 } from 'lucide-react';
import { useEffect, useMemo, type FC } from 'react';
import { Chat } from '../../../../Chat/components/Chat';

interface TableViewChatPanelProps {
  tableViewId: string;
  onClose: () => void;
}

export const TableViewChatPanel: FC<TableViewChatPanelProps> = ({
  tableViewId,
  onClose,
}) => {
  const { tableViewChat, isPending: isFetchingChat } =
    useGetTableViewChatService(tableViewId);

  const { createTableViewChat, isPending: isCreatingChat } =
    useCreateTableViewChatService();

  // Create chat if it doesn't exist when panel is mounted
  useEffect(() => {
    if (!isFetchingChat && !isCreatingChat && tableViewChat === null) {
      createTableViewChat({ tableViewId });
    }
  }, [
    isFetchingChat,
    isCreatingChat,
    tableViewChat,
    tableViewId,
    createTableViewChat,
  ]);

  const chatId = tableViewChat?.id;
  const initialMessages = tableViewChat?.messages;

  const { messages, sendMessage, status, error } = useTableViewChat(
    tableViewId,
    initialMessages
  );

  const lastMessage = messages[messages.length - 1];

  const isThinking =
    status === 'submitted' ||
    (status === 'streaming' && lastMessage?.role === 'user');

  const isDisabled = status !== 'ready' || isCreatingChat || !chatId;

  const isLoading = isFetchingChat || isCreatingChat;

  const chatData = useMemo(() => {
    if (!tableViewChat) return null;
    return {
      id: tableViewChat.id,
      createdAt: tableViewChat.createdAt,
      title: tableViewChat.title ?? undefined,
    };
  }, [tableViewChat]);

  return (
    <FlexCol className="w-[400px] h-full border-r border-neutral-300 bg-white flex-shrink-0 m-2 py-2 overflow-hidden">
      {isLoading ? (
        <div className="flex items-center justify-center h-full">
          <span className="text-neutral-500">Loading...</span>
        </div>
      ) : (
        <Chat
          messages={messages}
          chatData={chatData}
          sendMessage={sendMessage}
          showFollowUpQuestions={false}
          className="h-full w-full overflow-auto"
        >
          <Chat.Layout className="py-3 w-full px-2">
            <Chat.Content className="w-full scrollbar-hide mb-2 !max-w-none !px-2">
              {messages.length === 0 ? (
                <FlexCol
                  gap="4"
                  px="10"
                  h="full"
                  w="full"
                  alignItems="center"
                  justifyContent="center"
                >
                  <FlexCol gap="1.5" alignItems="center">
                    <FlexRow
                      gap="2"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text variant="body-sm" color="secondary">
                        Ask questions about the data in this table
                      </Text>
                    </FlexRow>
                  </FlexCol>
                </FlexCol>
              ) : (
                messages.map((message, idx) => (
                  <Chat.TableViewChatMessage
                    key={message.id || idx}
                    message={message}
                    messageIndex={idx}
                    isLastMessage={idx === messages.length - 1}
                    onFeedback={() => {}}
                  />
                ))
              )}
              {isThinking && (
                <FlexRow
                  gap="2"
                  alignItems="start"
                  justifyContent="center"
                  className="py-4 h-full w-full"
                >
                  <Loader2 className="h-4 w-4 animate-spin text-neutral-500" />
                </FlexRow>
              )}
              {error && <Chat.ErrorMessage />}
            </Chat.Content>
            <Chat.ScrollToBottomButton />
            <FlexCol gap="4" w="full">
              <Chat.Input
                type="homepage"
                onSubmit={sendMessage}
                disabled={isDisabled}
                placeholder="Ask anything"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={onClose}
                className="w-full justify-between px-3 py-[5px] text-neutral-900"
              >
                Hide chat
                <Minimize2 className="w-2 h-2" />
              </Button>
            </FlexCol>
          </Chat.Layout>
        </Chat>
      )}
    </FlexCol>
  );
};
